import { useEffect, useRef } from 'react';
import { useAnchoredPlacement } from './useAnchoredPlacement';

// The confirmation a finger gets before a bar drag writes dates (T402,
// Planning/GANTT-DRAG-CONFIRM-2026-08-30.md).
//
// Bill, 2026-08-30, from iPad testing: "The only thing you have to be super
// careful on is dragging a task/object around I was just testing it and
// accidently moved one of the objects dates.. maybe worth adding a confirmation
// prompt when you change the dates in the Gantt otherwise it just does it and
// you don't really know what you've changed."
//
// ⛔ ANCHORED AT THE BAR, NOT A CENTRE-SCREEN MODAL - his choice of the two
// mock-ups he was shown. The question is about a bar that has just moved, so the
// bar has to stay on screen beside it; a backdrop-and-dialog covers the one
// thing the answer is about.
//
// ⛔ IT PRINTS BOTH RANGES, old above new. "you don't really know what you've
// changed" is half the report, and a prompt reading only "are you sure?" answers
// none of it.
//
// ⛔ TOUCH ONLY. A mouse and an Apple Pencil commit straight through the chart's
// own release path, which is where that branch lives - this component is never
// rendered with a value for them.
//
// ⛔ AND IT KNOWS NOTHING ABOUT TASKS OR DATES. The formatter and the count
// sentence are props, because one chart prints 03/09/2026 and the other prints
// W1D3, and neither is this component's business.
export default function GanttDragConfirm({ confirm, formatDate, countLabel, onCancel, onConfirm }) {
    const { ref, placed } = useAnchoredPlacement(confirm);

    // ⛔ THE FOCUS GOES TO CANCEL, AND CANCEL IS THE SAFE ANSWER. Enter then
    // means "put it back", which is the direction this whole feature runs in.
    //
    // ⚠️ Keyed on `confirm`, a fresh object per question, so a second drag
    // re-focuses rather than leaving the focus on a stale button.
    const cancelRef = useRef(null);
    useEffect(() => {
        if (confirm) cancelRef.current?.focus();
    }, [confirm]);

    // ⛔ D13's SETTLING GUARD. The popup opens from a pointerup, and near the top
    // of the viewport it flips BELOW its anchor - putting these buttons exactly
    // where the finger just was. Two things should already stop that gesture
    // pressing one (the pointerdown was preventDefault'ed, and no browser
    // synthesises a tap-click after 24px of travel), but neither is testable in
    // jsdom and neither is provable off a real iPad. So a click arriving within
    // 250ms of the question opening is discarded.
    //
    // ⚠️ A REF, NOT STATE: this must not cause a render, and the handler must
    // read it without closing over a stale copy.
    //
    // ⚠️ AND IT GUARDS THE BUTTONS ONLY. Escape and the backdrop CANCEL, and a
    // stray cancel costs nothing - the bar goes back and nothing is written.
    const openedAtRef = useRef(0);
    useEffect(() => {
        if (confirm) openedAtRef.current = Date.now();
    }, [confirm]);
    const settled = () => Date.now() - openedAtRef.current >= 250;

    // ⛔ ESCAPE CANCELS, IT DOES NOT MERELY CLOSE. Dismissing without an answer
    // must put the bar back - a dismissal that silently kept the new dates would
    // be the reported bug with one extra step in front of it.
    //
    // ⚠️ ON THE DOCUMENT, not on the card, even though the card takes the focus:
    // the finger that opened this popup has focused nothing, and a key pressed
    // with the focus anywhere else must still cancel.
    useEffect(() => {
        if (!confirm) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [confirm, onCancel]);

    if (!confirm) return null;

    const { row, was, next, dragType } = confirm;

    return (
        <>
            {/* A press anywhere else cancels - see the Escape note above.

                ⛔ onPointerDown, NOT onClick. The three popups in GanttPopups.js
                use the click because they open from a ctrl+click or a
                right-click and are desktop paths; this one exists ONLY for a
                finger, and iOS synthesises its compatibility mouse events late.
                Those synthesised events are mouse events and produce no pointer
                events, which is also why the release that OPENS this popup
                cannot immediately close it. Do not tidy it to match its
                neighbours. */}
            <div
                data-testid="drag-confirm-backdrop"
                onPointerDown={onCancel}
                style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            />

            {/* ⛔ NO aria-modal, DELIBERATELY, AND IT IS NOT AN OVERSIGHT. There
                is no focus trap here, so the attribute would tell assistive
                technology that everything outside is inert while Tab still walks
                straight out of it. The backdrop makes the chart inert to
                POINTERS and to nothing else. Adding the attribute means adding
                the trap in the same edit. */}
            <div
                ref={ref}
                data-testid="gantt-drag-confirm"
                role="dialog"
                aria-label={`Change the dates for ${row?.text || 'this task'}`}
                className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                style={{
                    position: 'fixed',
                    left: placed?.x ?? confirm.x,
                    top: placed?.y ?? confirm.y,
                    transform: placed?.arrowPosition === 'top' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
                    zIndex: 9999,
                    visibility: placed ? 'visible' : 'hidden'
                }}
            >
                <p className="text-sm font-semibold text-gray-900">{row?.text}</p>
                <p className="mt-1 text-sm text-gray-500 line-through" data-testid="drag-confirm-was">
                    {formatDate(was.start)} – {formatDate(was.end)}
                </p>
                <p className="text-sm font-semibold text-gray-900" data-testid="drag-confirm-next">
                    {formatDate(next.start)} – {formatDate(next.end)}
                </p>
                {/* ⚠️ Rendered only when there is something to say. An empty
                    "0 linked tasks" is noise on a popup meant to be read in one
                    glance, which is why the chart passes null rather than 0. */}
                {countLabel && (
                    <p className="mt-1 text-sm text-gray-600" data-testid="drag-confirm-count">
                        {countLabel}
                    </p>
                )}

                {/* ⛔ min-h-[44px] IS T369's OWN FIGURE, and this popup exists
                    only for a finger. A py-1 button inside a prompt about
                    mis-taps is the reported bug arriving through the door it was
                    built to shut. */}
                <div className="mt-3 flex justify-end gap-2">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={() => { if (settled()) onCancel(); }}
                        className="min-h-[44px] rounded border border-gray-300 px-4 text-sm text-gray-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => { if (settled()) onConfirm(); }}
                        className="min-h-[44px] rounded bg-primary-600 px-4 text-sm font-semibold text-white"
                    >
                        {dragType === 'move' ? 'Move it' : 'Resize it'}
                    </button>
                </div>
            </div>
        </>
    );
}
